import React, { useEffect, useState, useCallback } from 'react';
import {
  Eye,
  Heart,
  Share2,
  MessageCircle,
  Bookmark,
  Search,
  RefreshCw,
  Sparkles,
  Image as ImageIcon,
  ExternalLink,
  Loader2,
  Wand2,
  ChevronRight,
  TrendingUp,
  Video,
  Play,
  Film,
  Download,
} from 'lucide-react';
import { supabase } from '../lib/supabase';

interface TiktokAnalysis {
  id: string;
  username: string;
  nickname: string | null;
  creator_link: string;
  post_id: string;
  post_url: string;
  view_count: number | null;
  like_count: number | null;
  comment_count: number | null;
  share_count: number | null;
  collect_count: number | null;
  sound_title: string | null;
  sound_author: string | null;
  video_text: string | null;
  thumbnail_url: string | null;
  storage_path: string | null;
  background_type: string | null;
  text_hook: string | null;
  image_description: string | null;
  in_campaign_csv: boolean;
}

interface UgcGenerated {
  id: string;
  original_post_id: string;
  generated_image_url: string;
  prompt: string;
  status: 'pending' | 'generating' | 'success' | 'failed';
  created_at: string;
  kie_task_id: string;
}

interface UgcVideo {
  id: string;
  original_post_id: string;
  generated_video_url: string | null;
  prompt: string | null;
  status: 'pending' | 'generating' | 'success' | 'failed';
  created_at: string;
  kie_task_id: string;
}

const STORAGE_BASE = 'https://wrlgoxbzlngdtomjhvnz.supabase.co/storage/v1/object/public/tiktok-bots/';
const PAGE_SIZE = 20;

export const UGC: React.FC = () => {
  const [videos, setVideos] = useState<TiktokAnalysis[]>([]);
  const [generated, setGenerated] = useState<Map<string, UgcGenerated>>(new Map());
  const [generatedVideos, setGeneratedVideos] = useState<Map<string, UgcVideo>>(new Map());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [bgFilter, setBgFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState<TiktokAnalysis | null>(null);
  const [generating, setGenerating] = useState<string | null>(null);
  const [animating, setAnimating] = useState<string | null>(null);
  const [bgTypes, setBgTypes] = useState<string[]>([]);

  const fetchVideos = useCallback(async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('tiktok_account_analysis')
        .select('*', { count: 'exact' })
        .order('view_count', { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      if (bgFilter !== 'all') {
        query = query.ilike('background_type', `%${bgFilter}%`);
      }
      if (search) {
        query = query.or(`text_hook.ilike.%${search}%,username.ilike.%${search}%,image_description.ilike.%${search}%`);
      }

      const { data, error, count } = await query;
      if (error) throw error;
      setVideos((data as unknown as TiktokAnalysis[]) || []);
      setTotalCount(count || 0);
    } catch (err) {
      console.error('Error fetching videos:', err);
    } finally {
      setLoading(false);
    }
  }, [page, bgFilter, search]);

  const fetchBgTypes = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('tiktok_account_analysis')
        .select('background_type')
        .not('background_type', 'is', null);
      if (error) return;
      const types = [...new Set((data || []).map((d: any) => d.background_type).filter(Boolean))]
        .sort();
      setBgTypes(types.slice(0, 30));
    } catch {}
  }, []);

  const fetchGenerated = useCallback(async () => {
    try {
      // Fetch ALL rows (success + generating) so we can resume polling for in-progress jobs
      const [imgRes, vidRes] = await Promise.all([
        supabase.from('ugc_generated_images').select('*').in('status', ['success', 'generating']),
        supabase.from('ugc_generated_videos').select('*').in('status', ['success', 'generating']),
      ]);

      const imgMap = new Map<string, UgcGenerated>();
      for (const row of (imgRes.data as unknown as UgcGenerated[]) || []) {
        if (!imgMap.has(row.original_post_id)) imgMap.set(row.original_post_id, row);
      }
      setGenerated(imgMap);

      const vidMap = new Map<string, UgcVideo>();
      for (const row of (vidRes.data as unknown as UgcVideo[]) || []) {
        if (!vidMap.has(row.original_post_id)) vidMap.set(row.original_post_id, row);
      }
      setGeneratedVideos(vidMap);

      // Resume polling for any in-progress jobs
      for (const row of (imgRes.data as unknown as UgcGenerated[]) || []) {
        if (row.status === 'generating' && row.kie_task_id && !generating) {
          resumeImagePoll(row.original_post_id, row.kie_task_id, row.prompt);
        }
      }
      for (const row of (vidRes.data as unknown as UgcVideo[]) || []) {
        if (row.status === 'generating' && row.kie_task_id && !animating) {
          resumeVideoPoll(row.original_post_id, row.kie_task_id);
        }
      }
    } catch {}
  }, []);

  // Resume polling for an in-progress image generation
  const resumeImagePoll = async (postId: string, taskId: string, prompt: string) => {
    setGenerating(postId);
    try {
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const fnUrl = `${supabaseUrl}/functions/v1/generate-ugc-image`;
      const { data: { session } } = await supabase.auth.getSession();
      const headers: Record<string, string> = { 'Content-Type': 'application/json', 'apikey': anonKey };
      if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;

      let attempts = 0;
      while (attempts < 60) {
        await new Promise(r => setTimeout(r, 3000));
        const pollRes = await fetch(fnUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify({ taskId, poll: true, post_id: postId, prompt }),
        });
        const pollData = await pollRes.json();
        if (pollData.status === 'success' && pollData.imageUrl) {
          await fetchGenerated();
          break;
        }
        if (pollData.status === 'failed') {
          await fetchGenerated();
          break;
        }
        attempts++;
      }
    } catch {} finally {
      setGenerating(null);
    }
  };

  // Resume polling for an in-progress video generation
  const resumeVideoPoll = async (postId: string, taskId: string) => {
    setAnimating(postId);
    try {
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const fnUrl = `${supabaseUrl}/functions/v1/generate-ugc-image`;
      const { data: { session } } = await supabase.auth.getSession();
      const headers: Record<string, string> = { 'Content-Type': 'application/json', 'apikey': anonKey };
      if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;

      let attempts = 0;
      while (attempts < 80) {
        await new Promise(r => setTimeout(r, 5000));
        const pollRes = await fetch(fnUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify({ taskId, poll_video: true, post_id: postId }),
        });
        const pollData = await pollRes.json();
        if (pollData.status === 'success' && pollData.videoUrl) {
          await fetchGenerated();
          break;
        }
        if (pollData.status === 'failed') {
          await fetchGenerated();
          break;
        }
        attempts++;
      }
    } catch {} finally {
      setAnimating(null);
    }
  };

  useEffect(() => {
    fetchVideos();
    fetchBgTypes();
    fetchGenerated();
  }, [fetchVideos, fetchBgTypes, fetchGenerated]);

  const handleGenerate = async (video: TiktokAnalysis) => {
    setGenerating(video.post_id);
    // Immediately clear old image from local state so spinner shows
    setGenerated(prev => {
      const next = new Map(prev);
      const existing = next.get(video.post_id);
      if (existing) {
        next.set(video.post_id, { ...existing, generated_image_url: null, status: 'generating' });
      }
      return next;
    });
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const fnUrl = `${supabaseUrl}/functions/v1/generate-ugc-image`;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'apikey': anonKey,
      };
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const res = await fetch(fnUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          post_id: video.post_id,
          background_type: video.background_type,
          image_description: video.image_description,
          text_hook: video.text_hook,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to start generation');

      // Poll for result
      const taskId = data.taskId;
      let attempts = 0;
      while (attempts < 60) {
        await new Promise(r => setTimeout(r, 3000));
        const pollRes = await fetch(fnUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify({ taskId, poll: true, post_id: video.post_id, prompt: data.prompt }),
        });
        const pollData = await pollRes.json();
        if (pollData.status === 'success' && pollData.imageUrl) {
          await fetchGenerated();
          break;
        }
        if (pollData.status === 'failed') throw new Error(pollData.error || 'Generation failed');
        attempts++;
      }
    } catch (err) {
      console.error('Generation error:', err);
      alert(`Failed to generate: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setGenerating(null);
    }
  };

  const handleAnimate = async (video: TiktokAnalysis) => {
    setAnimating(video.post_id);
    // Immediately clear old video from local state so spinner shows
    setGeneratedVideos(prev => {
      const next = new Map(prev);
      const existing = next.get(video.post_id);
      if (existing) {
        next.set(video.post_id, { ...existing, generated_video_url: null, status: 'generating' });
      }
      return next;
    });
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const fnUrl = `${supabaseUrl}/functions/v1/generate-ugc-image`;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'apikey': anonKey,
      };
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      // Start animation
      const res = await fetch(fnUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({ post_id: video.post_id, animate: true }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to start animation');

      // Poll for video result
      const taskId = data.taskId;
      let attempts = 0;
      while (attempts < 80) {
        await new Promise(r => setTimeout(r, 5000));
        const pollRes = await fetch(fnUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify({ taskId, poll_video: true, post_id: video.post_id }),
        });
        const pollData = await pollRes.json();
        if (pollData.status === 'success' && pollData.videoUrl) {
          await fetchGenerated();
          break;
        }
        if (pollData.status === 'failed') throw new Error(pollData.error || 'Animation failed');
        attempts++;
      }
    } catch (err) {
      console.error('Animation error:', err);
      alert(`Failed to animate: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setAnimating(null);
    }
  };

  const thumbnailUrl = (video: TiktokAnalysis) => {
    if (video.storage_path) return `${STORAGE_BASE}${video.storage_path}`;
    return video.thumbnail_url || '';
  };

  // Cache-busting helper — appends ?v=taskId to force browser to fetch new image
  // Uses kie_task_id so the URL only changes when a new image is actually generated
  const cacheBust = (url: string | null | undefined, taskId?: string) => {
    if (!url) return '';
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}v=${taskId || Date.now()}`;
  };

  const formatNum = (n: number | null) => {
    if (n === null || n === undefined) return '—';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return String(n);
  };

  return (
    <div className="flex h-full" style={{ backgroundColor: '#111111' }}>
      {/* Left panel — original videos list */}
      <div className="flex flex-col" style={{ width: '55%', minWidth: 400 }}>
        {/* Header / filters */}
        <div className="sticky top-0 z-10 p-4 border-b border-gray-800" style={{ backgroundColor: '#161616' }}>
          <div className="flex items-center gap-3 mb-3">
            <Sparkles className="text-purple-400" size={22} />
            <h1 className="text-xl font-bold text-white">UGC Generator</h1>
            <span className="text-sm text-gray-500">({totalCount} videos)</span>
            <button
              onClick={fetchVideos}
              className="ml-auto p-1.5 rounded-lg hover:bg-gray-800 text-gray-400"
              title="Refresh"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
          <div className="flex gap-2 items-center">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={14} />
              <input
                type="text"
                placeholder="Search hooks, accounts, descriptions..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(0); }}
                className="w-full pl-9 pr-3 py-1.5 text-sm rounded-lg bg-gray-800 text-white border border-gray-700 focus:border-purple-500 outline-none"
              />
            </div>
            <select
              value={bgFilter}
              onChange={e => { setBgFilter(e.target.value); setPage(0); }}
              className="flex-shrink-0 px-3 py-1.5 text-sm rounded-lg bg-gray-800 text-white border border-gray-700 focus:border-purple-500 outline-none max-w-[40%]"
            >
              <option value="all">All backgrounds</option>
              {bgTypes.map(bg => (
                <option key={bg} value={bg}>{bg}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Video list */}
        <div className="flex-1 overflow-y-auto">
          {loading && videos.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-gray-500">
              <Loader2 className="animate-spin mr-2" size={20} /> Loading...
            </div>
          ) : (
            <div className="divide-y divide-gray-800">
              {videos.map((video, idx) => {
                const isSelected = selectedVideo?.post_id === video.post_id;
                const hasGenerated = generated.has(video.post_id) && generated.get(video.post_id)?.generated_image_url && generated.get(video.post_id)?.status === 'success';
                const isGenerating = generating === video.post_id || generated.get(video.post_id)?.status === 'generating';
                const thumb = thumbnailUrl(video);
                return (
                  <div
                    key={video.post_id}
                    onClick={() => setSelectedVideo(video)}
                    className={`flex gap-3 p-3 cursor-pointer transition-colors ${
                      isSelected ? 'bg-gray-800' : 'hover:bg-gray-850'
                    }`}
                    style={{ backgroundColor: isSelected ? '#1e1e2e' : undefined }}
                  >
                    {/* Thumbnail */}
                    <div className="relative flex-shrink-0">
                      {thumb ? (
                        <img
                          src={thumb}
                          alt={video.username}
                          className="w-16 h-24 rounded-lg object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-16 h-24 rounded-lg bg-gray-700 flex items-center justify-center">
                          <ImageIcon className="text-gray-500" size={20} />
                        </div>
                      )}
                      <span className="absolute -top-1 -left-1 px-1.5 py-0.5 text-xs font-bold rounded bg-purple-600 text-white">
                        {page * PAGE_SIZE + idx + 1}
                      </span>
                      {hasGenerated && (
                        <span className="absolute -bottom-1 -right-1 p-0.5 rounded-full bg-green-500" title="Generated">
                          <Wand2 className="text-white" size={10} />
                        </span>
                      )}
                      {isGenerating && !hasGenerated && (
                        <span className="absolute -bottom-1 -right-1 p-0.5 rounded-full bg-purple-500" title="Generating">
                          <Loader2 className="text-white animate-spin" size={10} />
                        </span>
                      )}
                      {generatedVideos.get(video.post_id)?.generated_video_url && generatedVideos.get(video.post_id)?.status === 'success' && (
                        <span className="absolute -top-1 -right-1 p-0.5 rounded-full bg-blue-500" title="Animated">
                          <Play className="text-white" size={10} />
                        </span>
                      )}
                      {(animating === video.post_id || generatedVideos.get(video.post_id)?.status === 'generating') && !generatedVideos.get(video.post_id)?.generated_video_url && (
                        <span className="absolute -top-1 -right-1 p-0.5 rounded-full bg-blue-500" title="Animating">
                          <Loader2 className="text-white animate-spin" size={10} />
                        </span>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-white">@{video.username}</span>
                        {video.in_campaign_csv && (
                          <span className="px-1.5 py-0.5 text-xs rounded bg-purple-900 text-purple-300">campaign</span>
                        )}
                        <a
                          href={video.post_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          className="ml-auto text-gray-500 hover:text-white"
                        >
                          <ExternalLink size={13} />
                        </a>
                      </div>

                      {/* Stats */}
                      <div className="flex items-center gap-3 text-xs text-gray-400 mb-1.5">
                        <span className="flex items-center gap-1"><Eye size={11} /> {formatNum(video.view_count)}</span>
                        <span className="flex items-center gap-1"><Heart size={11} /> {formatNum(video.like_count)}</span>
                        <span className="flex items-center gap-1"><Share2 size={11} /> {formatNum(video.share_count)}</span>
                        <span className="flex items-center gap-1"><MessageCircle size={11} /> {formatNum(video.comment_count)}</span>
                      </div>

                      {/* Text hook */}
                      {video.text_hook && (
                        <p className="text-xs text-gray-300 line-clamp-2 mb-1">
                          "{video.text_hook}"
                        </p>
                      )}

                      {/* Background type */}
                      {video.background_type && (
                        <p className="text-xs text-gray-500 truncate">
                          <span className="text-gray-600">BG:</span> {video.background_type}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalCount > PAGE_SIZE && (
            <div className="flex items-center justify-center gap-2 p-4">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1 text-sm rounded-lg bg-gray-800 text-white disabled:opacity-30"
              >
                Prev
              </button>
              <span className="text-sm text-gray-500">
                {page + 1} / {Math.ceil(totalCount / PAGE_SIZE)}
              </span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * PAGE_SIZE >= totalCount}
                className="px-3 py-1 text-sm rounded-lg bg-gray-800 text-white disabled:opacity-30"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Right panel — detail view + generated image */}
      <div className="flex-1 overflow-y-auto border-l border-gray-800" style={{ backgroundColor: '#0d0d0d' }}>
        {!selectedVideo ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-600">
            <Sparkles size={40} className="mb-3 opacity-30" />
            <p className="text-sm">Select a video to see details</p>
            <p className="text-xs mt-1">and generate UGC stills with Grok Imagine</p>
          </div>
        ) : (
          <div className="p-6">
            {/* Original video detail */}
            <div className="mb-6">
              <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <TrendingUp size={18} className="text-purple-400" />
                Original Video
              </h2>

              <div className="flex gap-4">
                {/* Original thumbnail */}
                <div className="flex-shrink-0">
                  {thumbnailUrl(selectedVideo) ? (
                    <img
                      src={thumbnailUrl(selectedVideo)}
                      alt={selectedVideo.username}
                      className="w-32 h-48 rounded-xl object-cover border border-gray-700"
                    />
                  ) : (
                    <div className="w-32 h-48 rounded-xl bg-gray-800 flex items-center justify-center border border-gray-700">
                      <ImageIcon className="text-gray-600" size={24} />
                    </div>
                  )}
                </div>

                {/* Details */}
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <a
                      href={selectedVideo.creator_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-white hover:text-purple-400"
                    >
                      @{selectedVideo.username}
                    </a>
                    <a
                      href={selectedVideo.post_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-white"
                    >
                      <ExternalLink size={13} />
                    </a>
                  </div>

                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { label: 'Views', value: selectedVideo.view_count, icon: Eye },
                      { label: 'Likes', value: selectedVideo.like_count, icon: Heart },
                      { label: 'Shares', value: selectedVideo.share_count, icon: Share2 },
                      { label: 'Comments', value: selectedVideo.comment_count, icon: MessageCircle },
                    ].map(({ label, value, icon: Icon }) => (
                      <div key={label} className="rounded-lg bg-gray-850 p-2" style={{ backgroundColor: '#1a1a1a' }}>
                        <Icon className="text-gray-500 mb-1" size={13} />
                        <div className="text-sm font-bold text-white">{formatNum(value)}</div>
                        <div className="text-xs text-gray-500">{label}</div>
                      </div>
                    ))}
                  </div>

                  {selectedVideo.sound_title && (
                    <p className="text-xs text-gray-400">
                      <span className="text-gray-600">Sound:</span> {selectedVideo.sound_title}
                      {selectedVideo.sound_author && ` — ${selectedVideo.sound_author}`}
                    </p>
                  )}
                </div>
              </div>

              {/* Analysis sections */}
              <div className="mt-4 space-y-3">
                {selectedVideo.text_hook && (
                  <div className="rounded-lg p-3" style={{ backgroundColor: '#1a1a1a' }}>
                    <p className="text-xs text-gray-500 mb-1 font-medium">TEXT HOOK</p>
                    <p className="text-sm text-white">"{selectedVideo.text_hook}"</p>
                  </div>
                )}
                {selectedVideo.background_type && (
                  <div className="rounded-lg p-3" style={{ backgroundColor: '#1a1a1a' }}>
                    <p className="text-xs text-gray-500 mb-1 font-medium">BACKGROUND TYPE</p>
                    <p className="text-sm text-white">{selectedVideo.background_type}</p>
                  </div>
                )}
                {selectedVideo.image_description && (
                  <div className="rounded-lg p-3" style={{ backgroundColor: '#1a1a1a' }}>
                    <p className="text-xs text-gray-500 mb-1 font-medium">IMAGE DESCRIPTION (AI)</p>
                    <p className="text-sm text-gray-300">{selectedVideo.image_description}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-800 my-6" />

            {/* Generated UGC section */}
            <div>
              <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <Wand2 size={18} className="text-green-400" />
                Generated UGC Still
              </h2>

              {(() => {
                const gen = generated.get(selectedVideo.post_id);
                const hasImage = gen?.generated_image_url && gen.status === 'success';
                const isGenerating = generating === selectedVideo.post_id || gen?.status === 'generating';
                if (hasImage) {
                  return (
                <div className="space-y-3">
                  <div className="flex gap-4">
                    {/* Original */}
                    <div className="flex-shrink-0">
                      <p className="text-xs text-gray-500 mb-1">Original</p>
                      <img
                        src={thumbnailUrl(selectedVideo)}
                        alt="Original"
                        className="w-32 h-48 rounded-xl object-cover border border-gray-700"
                      />
                    </div>
                    {/* Arrow */}
                    <div className="flex items-center">
                      <ChevronRight className="text-gray-600" size={24} />
                    </div>
                    {/* Generated */}
                    <div className="flex-shrink-0">
                      <p className="text-xs text-green-400 mb-1">Grok Imagine</p>
                      <div className="relative group w-32 h-48">
                        <img
                          src={cacheBust(gen!.generated_image_url, gen!.kie_task_id)}
                          alt="Generated"
                          className="w-32 h-48 rounded-xl object-cover border border-green-700"
                        />
                        <div className="absolute inset-0 rounded-xl bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleGenerate(selectedVideo)}
                            title="Regenerate"
                            className="p-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white transition-colors"
                          >
                            <RefreshCw size={16} />
                          </button>
                          <a
                            href={cacheBust(gen!.generated_image_url, gen!.kie_task_id)}
                            download={`ugc-${selectedVideo.post_id}.png`}
                            title="Download"
                            className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white transition-colors"
                          >
                            <Download size={16} />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-lg p-3" style={{ backgroundColor: '#1a1a1a' }}>
                    <p className="text-xs text-gray-500 mb-1 font-medium">PROMPT</p>
                    <p className="text-xs text-gray-400 font-mono">{gen!.prompt}</p>
                  </div>
                  <a
                    href={cacheBust(gen!.generated_image_url, gen!.kie_task_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-purple-400 hover:text-purple-300"
                  >
                    <ExternalLink size={13} /> Open full image
                  </a>
                </div>
              );
                  }
                  if (isGenerating) {
                    return (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="animate-spin text-purple-400 mb-3" size={32} />
                  <p className="text-sm text-gray-400">Generating with Grok Imagine...</p>
                  <p className="text-xs text-gray-600 mt-1">This can take 30-90 seconds</p>
                </div>
                    );
                  }
                  return (
                <div className="flex flex-col items-center justify-center py-12">
                  <Wand2 className="text-gray-700 mb-3" size={32} />
                  <p className="text-sm text-gray-500 mb-3">No generated still yet</p>
                  <button
                    onClick={() => handleGenerate(selectedVideo)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium transition-colors"
                  >
                    <Sparkles size={15} />
                    Generate UGC Still
                  </button>
                  <p className="text-xs text-gray-600 mt-2">
                    Creates a raw phone-camera style 9:16 still matching this video's background
                  </p>
                </div>
                  );
              })()}
            </div>

            {/* Animated video section */}
            {generated.get(selectedVideo.post_id)?.generated_image_url && generated.get(selectedVideo.post_id)?.status === 'success' && (
              <>
                <div className="border-t border-gray-800 my-6" />
                <div>
                  <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                    <Film size={18} className="text-blue-400" />
                    Animated Video (10s)
                  </h2>

                  {(() => {
                    const vid = generatedVideos.get(selectedVideo.post_id);
                    if (vid?.generated_video_url) {
                      return (
                        <div className="space-y-3">
                          <div className="relative group w-48 mx-auto">
                            <video
                              src={cacheBust(vid.generated_video_url, vid.kie_task_id)}
                              controls
                              autoPlay
                              loop
                              muted
                              playsInline
                              className="w-48 h-auto rounded-xl border border-blue-700"
                            />
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                              <button
                                onClick={() => handleAnimate(selectedVideo)}
                                title="Re-animate"
                                className="p-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white transition-colors shadow-lg"
                              >
                                <RefreshCw size={16} />
                              </button>
                              <a
                                href={cacheBust(vid.generated_video_url, vid.kie_task_id)}
                                download={`ugc-${selectedVideo.post_id}.mp4`}
                                title="Download"
                                className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white transition-colors shadow-lg"
                              >
                                <Download size={16} />
                              </a>
                            </div>
                          </div>
                          <div className="flex items-center justify-center gap-3">
                            <a
                              href={cacheBust(vid.generated_video_url, vid.kie_task_id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300"
                            >
                              <ExternalLink size={13} /> Open video
                            </a>
                          </div>
                          {vid.prompt && (
                            <div className="rounded-lg p-3" style={{ backgroundColor: '#1a1a1a' }}>
                              <p className="text-xs text-gray-500 mb-1 font-medium">MOTION PROMPT</p>
                              <p className="text-xs text-gray-400 font-mono">{vid.prompt}</p>
                            </div>
                          )}
                        </div>
                      );
                    }
                    if (animating === selectedVideo.post_id || vid?.status === 'generating') {
                      return (
                        <div className="flex flex-col items-center justify-center py-12">
                          <Loader2 className="animate-spin text-blue-400 mb-3" size={32} />
                          <p className="text-sm text-gray-400">Animating with Grok Imagine...</p>
                          <p className="text-xs text-gray-600 mt-1">This can take 2-5 minutes</p>
                        </div>
                      );
                    }
                    if (vid?.status === 'failed') {
                      return (
                        <div className="flex flex-col items-center justify-center py-12">
                          <p className="text-sm text-red-400 mb-3">Animation failed</p>
                          <button
                            onClick={() => handleAnimate(selectedVideo)}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
                          >
                            <Film size={15} /> Try Again
                          </button>
                        </div>
                      );
                    }
                    return (
                      <div className="flex flex-col items-center justify-center py-12">
                        <Film className="text-gray-700 mb-3" size={32} />
                        <p className="text-sm text-gray-500 mb-3">No animated video yet</p>
                        <button
                          onClick={() => handleAnimate(selectedVideo)}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
                        >
                          <Play size={15} />
                          Animate (10s video)
                        </button>
                        <p className="text-xs text-gray-600 mt-2">
                          Animates the generated still into a 10-second video via Grok Imagine
                        </p>
                      </div>
                    );
                  })()}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
